// /app/frontend/src/components/PhotoWallMosaic.jsx
/**
 * PhotoWallMosaic - Mosaic display of participant photos
 * The more photos there are, the smaller each "pixel" becomes
 * Photos fill a square grid and animate smoothly
 */
import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../lib/utils';
import { Camera, User } from 'lucide-react';
import api from '../lib/api';

export default function PhotoWallMosaic({ drawId, className = "" }) {
  const [photos, setPhotos] = useState([]);
  const [focusedPhoto, setFocusedPhoto] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const containerRef = useRef(null);
  
  // Calculate grid size based on photo count
  const getGridSize = (count) => {
    if (count <= 4) return 2;
    if (count <= 9) return 3;
    if (count <= 16) return 4;
    if (count <= 25) return 5;
    if (count <= 36) return 6;
    if (count <= 49) return 7;
    if (count <= 64) return 8;
    if (count <= 81) return 9;
    if (count <= 100) return 10;
    return Math.ceil(Math.sqrt(count));
  };

  // Load photos initially and poll for new ones
  useEffect(() => {
    loadPhotos();
    
    // Poll for new photos every 5 seconds
    const interval = setInterval(() => {
      loadPhotos(true);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [drawId]);

  // Auto-focus rotation
  useEffect(() => {
    if (photos.length === 0) return;
    
    // Focus on a random photo every 4 seconds
    const focusInterval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * photos.length);
      setFocusedPhoto(photos[randomIndex]);
      
      // Clear focus after 3.5 seconds
      setTimeout(() => {
        setFocusedPhoto(null);
      }, 3500);
    }, 4000);
    
    return () => clearInterval(focusInterval);
  }, [photos]);

  const loadPhotos = async (polling = false) => {
    try {
      const params = polling && lastFetch ? `?since=${lastFetch}` : '';
      const res = await api.get(`/public/draw/${drawId}/photo-wall/photos${params}`);
      
      if (polling && res.data.length > 0) {
        // Add new photos with animation
        setPhotos(prev => [...res.data, ...prev]);
      } else if (!polling) {
        setPhotos(res.data);
      }
      
      if (res.data.length > 0) {
        setLastFetch(res.data[0].created_at);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  const gridSize = getGridSize(photos.length);
  const cellSize = `${100 / gridSize}%`;

  if (photos.length === 0) {
    return (
      <div className={`relative bg-black/20 rounded-2xl overflow-hidden ${className}`}>
        <div className="aspect-square flex flex-col items-center justify-center text-white/40">
          <Camera className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-lg">En attente de photos...</p>
          <p className="text-sm mt-2">Scannez le QR code pour partager</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Main Mosaic Grid */}
      <div 
        ref={containerRef}
        className="aspect-square bg-black/30 rounded-2xl overflow-hidden relative"
      >
        <div 
          className="grid w-full h-full gap-0.5"
          style={{ 
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gridTemplateRows: `repeat(${gridSize}, 1fr)`
          }}
        >
          {photos.slice(0, gridSize * gridSize).map((photo, index) => (
            <div
              key={photo.id}
              className={`relative overflow-hidden transition-all duration-500 ${
                focusedPhoto?.id === photo.id 
                  ? 'z-50 scale-150 shadow-2xl rounded-lg' 
                  : 'hover:scale-105'
              }`}
              style={{
                animationDelay: `${index * 50}ms`
              }}
            >
              {photo.media_type === 'video' ? (
                <video
                  src={getImageUrl(photo.url)}
                  className="w-full h-full object-cover"
                  muted
                  autoPlay
                  loop
                  playsInline
                />
              ) : (
                <img
                  src={getImageUrl(photo.url)}
                  alt={photo.pseudo}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
              
              {/* Message overlay (shown on focus) */}
              {focusedPhoto?.id === photo.id && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-2">
                  {photo.message && (
                    <p className="text-white text-xs font-medium mb-1 line-clamp-2 bg-black/40 px-2 py-1 rounded">
                      {photo.message}
                    </p>
                  )}
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-white text-xs font-bold">{photo.pseudo}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* New photo indicator */}
        <div className="absolute top-3 right-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 animate-pulse">
          <span className="w-2 h-2 bg-white rounded-full"></span>
          LIVE
        </div>
        
        {/* Photo count */}
        <div className="absolute bottom-3 left-3 bg-black/50 backdrop-blur text-white text-sm px-3 py-1 rounded-full flex items-center gap-2">
          <Camera className="w-4 h-4" />
          <span>{photos.length} photos</span>
        </div>
      </div>

      {/* Focused Photo Overlay */}
      {focusedPhoto && (
        <div 
          className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none"
          style={{ backgroundColor: 'transparent' }}
        >
          <div 
            className="w-[50vmin] h-[50vmin] rounded-2xl overflow-hidden shadow-2xl animate-scale-in"
            style={{
              animation: 'scaleIn 0.3s ease-out'
            }}
          >
            {focusedPhoto.media_type === 'video' ? (
              <video
                src={getImageUrl(focusedPhoto.url)}
                className="w-full h-full object-cover"
                muted
                autoPlay
                loop
                playsInline
              />
            ) : (
              <img
                src={getImageUrl(focusedPhoto.url)}
                alt={focusedPhoto.pseudo}
                className="w-full h-full object-cover"
              />
            )}
            
            {/* Message & Author */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-4">
              {focusedPhoto.message && (
                <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-lg mb-3">
                  <p className="text-white text-lg font-medium">
                    "{focusedPhoto.message}"
                  </p>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <span className="text-white text-xl font-bold">{focusedPhoto.pseudo}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes scaleIn {
          from {
            transform: scale(0.8);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
